/**
 * resources/lib/deliveryFees.js — pricing-v3 delivery fee engine (pure).
 *
 * Model decided 2026-09-02 (Luke), replacing the per-visit "Model F" locked
 * 2026-07-17. The unit of charge is the CRATE, not the visit:
 *
 *   · Retrieval is `base_crate_fee_cents` ($15) PER CRATE.
 *   · The 4-month waiver is CRATE-SPECIFIC. Each crate earns it on its own
 *     maturity date; a matured crate waives its own $15 and nobody else's.
 *   · Scheduling premiums are flat PER VISIT, because what they buy is route
 *     scarcity, not handling. They ADD to the crate fees, they do not replace
 *     them, and neither is touched by the waiver:
 *       exact day : exact_day_adder_cents      (+$10 per visit)
 *       peak week : adder_cents                (+$15 per visit)
 *   · Free everywhere, no engine involvement: new-crate drop-offs, send-in
 *     pickups, empty-crate retrievals (BookEmptyRetrieval).
 *
 * WHAT CHANGED AND WHY IT MATTERED
 * v2 charged one fee per visit and waived it if ANY crate in the booking had
 * matured. That let a customer mature a single crate, add fifteen fresh ones,
 * and retrieve all sixteen for nothing. The leak was only $15 per booking
 * (the whole visit was $15), but the rule was also written into the Terms,
 * so it had to be fixed in both places at once. See legal/tos-2026-09-16.md
 * §7 "Four-month waiver", which now says the waiver is crate-specific.
 *
 * `visits` survives for the premiums only. The app caps a booking at
 * `truckload_crates`, so in practice it is always 1; the engine still divides
 * because it is charge-authoritative and must not under-bill if that cap is
 * ever bypassed.
 *
 * This module is display-AND-charge authoritative: FeeSchedule/preview and
 * the booking resources must both call it so the number the customer saw is
 * the number they're charged. No I/O, no Date.now() — callers inject today.
 */

/** Fallback config — mirrors the seeded PricingConfig row. */
export const DEFAULT_PRICING_CONFIG = Object.freeze({
  base_crate_fee_cents: 1500,
  exact_day_adder_cents: 1000,
  default_peak_adder_cents: 1500,
  return_booked_credit_cents: 500,
  free_after_months: 4,
  truckload_crates: 16,
  booking_horizon_days: 365,
  // ASAP pickup promise (pricing v2, 2026-07-18): business-day leads.
  pickup_lead_business_days: 2,
  peak_pickup_lead_business_days: 5,
})

/**
 * Read a PricingConfig row, accepting the v2 key names.
 *
 * 🔧 MIGRATION SHIM. The live rows in dev, staging and prod still carry
 * `base_visit_fee_cents` and `exact_day_fee_cents`. Renaming a column is a
 * data operation on three clusters; renaming it in code first, and reading
 * either shape, means the deploy does not have to be simultaneous with it.
 *
 * The exact-day translation is the subtle one. In v2 that number was the
 * whole exact-day fee and REPLACED the base ($25 instead of $15). In v3 it is
 * a premium ADDED to the crate fees, so the equivalent value is the
 * difference: $25 - $15 = $10, which is exactly the decided adder. Deriving
 * it rather than defaulting keeps a hand-tuned row meaning what its author
 * intended instead of silently snapping to $10.
 *
 * DELETE THIS once all three PricingConfig rows use the v3 names.
 */
export function normalizePricingConfig(row) {
  if (!row) return { ...DEFAULT_PRICING_CONFIG }

  // 🔴 Explicit per-key reads, NEVER `{ ...row }`. A Harper `.get()` record
  // does not enumerate, so spreading one yields the bare defaults and silently
  // drops every stored value. Both callers hand this function exactly such a
  // record: AdminPricing.ensureConfig and FeeSchedule.ensureConfig each return
  // the result of `db.PricingConfig.get(...)`. AdminPricing already carried
  // that warning on its own loop; this function did not, so on a MIGRATED v3
  // row FeeSchedule would have published the default fees rather than the
  // stored ones — the same class of bug as the v2 one below, in the opposite
  // direction, and invisible to a unit test that mocks the row as a plain
  // object.
  const cfg = {}
  for (const key of Object.keys(DEFAULT_PRICING_CONFIG)) {
    const stored = row[key]
    cfg[key] = stored === undefined || stored === null ? DEFAULT_PRICING_CONFIG[key] : stored
  }

  if (row.base_crate_fee_cents == null && row.base_visit_fee_cents != null) {
    cfg.base_crate_fee_cents = row.base_visit_fee_cents
  }
  if (row.exact_day_adder_cents == null && row.exact_day_fee_cents != null) {
    cfg.exact_day_adder_cents = Math.max(0, row.exact_day_fee_cents - cfg.base_crate_fee_cents)
  }
  return cfg
}

/**
 * Add CALENDAR months, clamping to the last valid day of the target month
 * (Oct 31 + 4mo → Feb 28/29, never Mar 1..3). Mirrors src/utils/pricing.ts
 * addCalendarMonths and Stripe's anchor behavior. Date-only math on UTC to
 * avoid TZ drift on YYYY-MM-DD strings.
 */
export function addCalendarMonthsYmd(ymd, months) {
  const [y, m, d] = ymd.split('-').map(Number)
  const targetIndex = m - 1 + months
  const ty = y + Math.floor(targetIndex / 12)
  const tm = ((targetIndex % 12) + 12) % 12
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate()
  const td = Math.min(d, lastDay)
  return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(td).padStart(2, '0')}`
}

/**
 * Has this crate earned its retrieval-fee waiver by `asOfYmd`?
 * Maturity = stored_at + free_after_months calendar months. We evaluate
 * against the END of the chosen window (or the exact day) so a crate that
 * matures mid-window still earns the waiver — deterministic at booking time
 * and customer-favorable.
 */
export function isCrateMaturedBy(storedAt, asOfYmd, freeAfterMonths) {
  if (!storedAt || typeof storedAt !== 'string') return false
  const storedYmd = storedAt.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(storedYmd)) return false
  return addCalendarMonthsYmd(storedYmd, freeAfterMonths) <= asOfYmd
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * Compute the fee for one delivery/retrieval booking.
 *
 * @param {object} args
 * @param {number}  args.crateCount            crates in the booking (≥1)
 * @param {Array<{stored_at?: string|null}>} [args.crates] per-crate maturity; see the note on previews below
 * @param {boolean} args.exactDay              customer picked a specific date
 * @param {string}  args.asOfYmd               window END date (or the exact day) — maturity + peak evaluated against the chosen slot
 * @param {{peak?: boolean, adder_cents?: number}|null} args.window the catalog window containing the chosen slot
 * @param {object} [args.config]               PricingConfig row (v2 or v3 key names)
 * @returns {{ visits, tier, crates_total, crates_waived, crates_charged,
 *             base_cents, base_waived, fee_waived_reason, exact_day_cents,
 *             peak_adder_cents, premiums_cents, total_cents, lines }}
 *          `lines` = customer-displayable breakdown [{label, cents}], summing
 *          exactly to `total_cents`.
 */
export function computeDeliveryFee({ crateCount, crates, exactDay, asOfYmd, window: win, config }) {
  const cfg = normalizePricingConfig(config)
  const count = Math.max(1, Math.floor(crateCount ?? crates?.length ?? 1))
  const visits = Math.ceil(count / cfg.truckload_crates)

  // Maturity is now per crate, so a caller that passes no `crates` array is
  // asking what a booking costs WITHOUT telling us which crates. Charging all
  // of them is the only safe answer: a preview that guessed a waiver would
  // under-quote, and assertPriceHandshake would then reject the real booking.
  const known = Array.isArray(crates) ? crates : []
  const matured = known.filter((c) => isCrateMaturedBy(c?.stored_at, asOfYmd, cfg.free_after_months))
  const waivedCount = Math.min(count, matured.length)
  const chargedCount = count - waivedCount

  const grossCents = count * cfg.base_crate_fee_cents
  const waivedCents = waivedCount * cfg.base_crate_fee_cents
  const baseCents = chargedCount * cfg.base_crate_fee_cents

  // Premiums are flat per visit and the waiver never touches them: what they
  // buy is a slot, and a matured crate did not pay for the slot in advance.
  const exactDayCents = exactDay ? cfg.exact_day_adder_cents * visits : 0
  const peak = !!win?.peak
  const adderCents = (peak ? (win.adder_cents ?? cfg.default_peak_adder_cents) : 0) * visits

  const total = baseCents + exactDayCents + adderCents

  const per = visits > 1 ? ` × ${visits} visits` : ''
  const lines = [{ label: `Retrieval, ${plural(count, 'crate')}`, cents: grossCents }]
  if (waivedCents > 0) {
    lines.push({
      label: `Fee waived, ${plural(waivedCount, 'crate')} stored ${cfg.free_after_months}+ months`,
      cents: -waivedCents,
    })
  }
  if (exactDayCents > 0) lines.push({ label: `Exact-day delivery${per}`, cents: exactDayCents })
  if (adderCents > 0) lines.push({ label: `Peak week${per}`, cents: adderCents })

  return {
    visits,
    tier: exactDay ? 'exact_day' : 'flexible_window',
    crates_total: count,
    crates_waived: waivedCount,
    crates_charged: chargedCount,
    base_cents: baseCents,
    // `base_waived` / `fee_waived_reason` stay whole-booking booleans because
    // they are persisted on the order and read by RescheduleOrder and
    // SchedulePickup. A PARTIAL waiver is not "waived" for those purposes; the
    // per-crate detail is in crates_waived and in `lines`.
    base_waived: chargedCount === 0,
    fee_waived_reason: chargedCount === 0 && waivedCount > 0 ? 'matured' : null,
    exact_day_cents: exactDayCents,
    peak_adder_cents: adderCents,
    // Renamed from v2's `per_visit_cents`, which meant base + adder back when
    // the base was itself per visit. It now means the scheduling premiums
    // only; the crate fees are in base_cents.
    premiums_cents: exactDayCents + adderCents,
    total_cents: total,
    lines,
  }
}
